<template>
<div class="chapter">
<div class="inner_page">
<p class="chapter-h"><span>Piecing</span></p>
<p class="section-1"><span>(<i>for Lois Sasson</i>)</span></p>
<p class="normal">“Sometimes you don’t have no control over the way things are. Hail ruins the crops, or fire burns you out. And then you’re just given so much to work with in a life and you have to do the best you can with what you got. That’s what piecing is. The materials is passed on to you, or is all you can afford. But the way you put them together is your business. You can put them in any order you like. Piecing is orderly.”</p>
<p class="right-1">An anonymous woman quoted</p>
<p class="right-1">in <i>The Quilters: Women and</i></p>
<p class="right-1"><i>Domestic Art</i></p>
<p class="hang-indent">Frugality is not the point. Nor waste.</p>
<p class="hang-indenta">It’s just that very little is discarded</p>
<p class="hang-indenta">in any honest spending of the self,</p>
<p class="hang-indenta">and what remains is used and used</p>
<p class="hang-indenta">again, worn thin by use, softened</p>
<p class="hang-indenta">to the pliancy and the translucence</p>
<p class="hang-indenta">of old linen, patched, mended, reinforced,</p>
<p class="hang-indenta">and saved. So I discover how</p>
<p class="hang-indenta">I am rejoicing slowly into a woman</p>
<p class="hang-indenta">who grows older daring to write</p>
<p class="hang-indenta">the same poem over and over, not merely</p>
<p class="hang-indenta">rearranged, revised, reworded, but one poem</p>
<p class="hang-indenta">hundreds of times anew.</p>
<p class="hang-indent">The gaudy anniversaries.</p>
<p class="hang-indenta">The strips of colorless days gone unexamined.</p>
<p class="hang-indenta">This piece of watered silk almost as shot with light</p>
<p class="hang-indenta">as a glance he gave me once. This sturdy</p>
<p class="hang-indenta">canvas shred of humor. That fragment of pearl velvet,</p>
<p class="hang-indenta">a particular snowstorm. Assorted samples of anger—</p>
<p class="hang-indenta">in oilcloth, in taffeta, in tufted chenille,</p>
<p class="hang-indenta">in every imaginable synthetic and ready-to-wear.</p>
<p class="hang-indenta">This diamond of tie-dyed flannel baby-blanket;</p>
<p class="hang-indenta">that other texture of deception, its dimensional embroidery.</p>
<p class="hang-indenta">A segment of bleached muslin still crisp with indifference.</p>
<p class="hang-indenta">This torn veil of chiffon, pewter as the rain</p>
<p class="hang-indenta">we wept through one entire July. These brightly printed</p>
<p class="hang-indenta">squares across which different familiar figures</p>
<p class="hang-indenta">walk through parks or juggle intricate abstract designs.</p>
<p class="hang-indenta">Two butterflies of yellow organdy my mother cut</p>
<p class="hang-indenta">when I was eight months old. A mango gros-grain ribbon</p>
<p class="hang-indenta">fading off toward peach. The corner of an old batik</p>
<p class="hang-indenta">showing one small window that looked out on—what?</p>
<p class="hang-indenta">A series of simple cotton triangles in primary colors.</p>
<p class="hang-indenta">And this octagonal oddment: a sunburst or mandala or pinwheel</p>
<p class="hang-indenta">radiating rainbow stripes against what turns out</p>
<p class="hang-indenta">upon close inspection to be a densely flowered background.</p>
<p class="hang-indenta">It’s striking enough to be a centerpiece.</p>
<p class="hang-indent">Once I thought this work could be less solitary.</p>
<p class="hang-indenta">Many of us, I imagined, would range ourselves</p>
<p class="hang-indenta">along the edges of some pattern we would all agree on</p>
<p class="hang-indenta">well beforehand, talking quietly while we worked</p>
<p class="hang-indenta">each with her unique stitch inward to the same shared center.</p>
<p class="hang-indent">This can still be done, of course, but some designs</p>
<p class="hang-indenta">emerge before they can be planned, much less agreed on,</p>
<p class="hang-indenta">demand an entire life’s work, and are best viewed upon completion.</p>
<p class="hang-indenta">And then, so many designers bore too easily</p>
<p class="hang-indenta">to work the same theme over and over, with only</p>
<p class="hang-indenta">the slightest gradual adjustments, like subtly changing</p>
<p class="hang-indenta">your thread from brown to gray.</p>
<p class="hang-indent">Still, the doorbell does toll in visitors, some of whom</p>
<p class="hang-indenta">slash rents across the section just perfected</p>
<p class="left-2">—all without meaning to,</p>
<p class="hang-indenta">and some of whom admire the audacity or quality</p>
<p class="hang-indenta">of scraps—but rarely notice the order, which is</p>
<p class="hang-indenta">the one thing you control. But some contribute:</p>
<p class="hang-indenta">a quarter yard of paisley, or a length of gauze</p>
<p class="hang-indenta">fine enough for bandages. Once somebody left behind</p>
<p class="hang-indenta">an entire pocket of gold lamé, all by itself.</p>
<p class="hang-indenta">The challenge is to use it so</p>
<p class="hang-indenta">that the tarnished griefs she stuffed it with</p>
<p class="hang-indenta">to lend it shape need be no longer hidden.</p>
<p class="hang-indent">Throwing such a piece away is not the answer. Nor</p>
<p class="hang-indenta">has hoarding anything to do with this.</p>
<p class="hang-indenta">And nobody really hazards piecework in the expectation</p>
<p class="hang-indenta">that someday all these fragments might inevitably</p>
<p class="right1-2"><span>fit</span></p>
<p class="hang-indenta">into a gentle billow of warmth, to comfort</p>
<p class="hang-indenta">the longest winter sleep.</p>
<p class="hang-indenta">Not even that.</p>
<p class="hang-indenta">It’s just the pleasure of rescuing some particle</p>
<p class="hang-indenta">into meaning. For a while.</p>
<p class="hang-indent">Of course, this means that you yourself</p>
<p class="hang-indenta">are placed where you risk being</p>
<p class="hang-indenta">worn all the more severely</p>
<p class="hang-indenta">into translucent linen, held up</p>
<p class="hang-indenta">toward the light.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter001.vue?8ffcee44